import PocketBase from 'pocketbase'

/**
 * The backend, as one client.
 *
 * `/pb` in every environment: the Vite dev server proxies it to a local
 * PocketBase, and Traefik strips the same prefix in production. So the browser
 * is always same-origin (no CORS) and the shipped bundle is the identical one
 * that ran in development, rather than one built with a different base URL. The
 * alternative was a build-time constant that, when wrong, builds perfectly and
 * 404s every request.
 *
 * Note what is NOT here: any notion of "not configured". The Supabase client was
 * nullable because it needed two environment variables that could be absent, and
 * every cloud path in the app opened with `if (!supabase) return`. There is no
 * such variable now, so there is no such state. localStorage remains the source
 * of truth for a character, so an unreachable backend degrades exactly the way a
 * failed write already did.
 */
export const pb = new PocketBase('/pb')

/**
 * Auto-cancellation off, deliberately.
 *
 * The SDK cancels an in-flight request when a second one shares its key, which
 * suits a search box and does not suit this app: the character sync is
 * fire-and-forget on a debounce, and a cancelled write surfaces as a
 * `ClientResponseError 0` that reads like a failure nobody caused. Off, the
 * later write simply wins, which is what the debounce already intended.
 */
pb.autoCancellation(false)

/** True when the error is the SDK reporting an autocancelled request rather than a refusal. */
export const isAbort = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { isAbort?: boolean }).isAbort === true

/**
 * Cloud writes are best-effort (localStorage is the source of truth), so they're
 * fired without awaiting. But swallowing their errors makes "saving doesn't work"
 * impossible to diagnose: a failed write looks identical to a successful one.
 * Pass this to `.catch(...)` on any fire-and-forget request to surface failures.
 */
export function logWriteError(context: string) {
  return (error: unknown) => {
    if (isAbort(error)) return
    console.error(`[pb] ${context} failed: ${messageOf(error)}`, error)
  }
}

/**
 * What went wrong, as something worth putting in front of a person.
 *
 * PocketBase answers a failed write with a `response` describing the offending
 * fields, and the top-level `message` is the unhelpful half of it: a username
 * clash arrives as "Failed to create record." with the reason one level down
 * under `data.name.message`. So the first field error wins, and the generic
 * message is the fallback rather than the answer.
 */
export function messageOf(error: unknown): string {
  if (typeof error !== 'object' || error === null) return String(error)
  const { response, message } = error as {
    response?: { message?: string; data?: Record<string, { message?: string }> }
    message?: string
  }
  const fields = response?.data
  if (fields) {
    for (const key of Object.keys(fields)) {
      const detail = fields[key]?.message
      if (detail) return detail
    }
  }
  return response?.message ?? message ?? 'Something went wrong'
}

/**
 * PocketBase's own id shape: 15 characters of `[a-z0-9]`.
 *
 * Generated in the browser rather than left to the server, because localStorage
 * is the source of truth: a character has an id the moment it is saved, offline,
 * long before any request goes out. Matching PocketBase's shape is what lets
 * that id BE the record id, so every later write is one call with the id in
 * hand. That is what replaces `upsert`, which PocketBase does not have.
 *
 * It also replaces a hand-rolled RFC 4122 v4 builder that existed only because
 * the old `id` column was typed `uuid` and `crypto.randomUUID` is undefined
 * outside a secure context. `getRandomValues` is not, and is all this needs.
 */
const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'
export const RECORD_ID_LENGTH = 15

export function newRecordId(): string {
  // Rejection sampling: 256 is not a multiple of 36, so taking the remainder of
  // every byte would make the first four letters likelier than the rest.
  const cutoff = 256 - (256 % ID_ALPHABET.length)
  let id = ''
  while (id.length < RECORD_ID_LENGTH) {
    for (const byte of crypto.getRandomValues(new Uint8Array(RECORD_ID_LENGTH))) {
      if (byte >= cutoff) continue
      id += ID_ALPHABET[byte % ID_ALPHABET.length]
      if (id.length === RECORD_ID_LENGTH) break
    }
  }
  return id
}

/**
 * Whether this id can be a PocketBase record id.
 *
 * Used to spot characters saved by an older build, whose ids are UUIDs and which
 * PocketBase would refuse. They are local-only by definition, since there was no
 * PocketBase to reach, so they are simply re-ided on load.
 */
export function isRecordId(id: string): boolean {
  return id.length === RECORD_ID_LENGTH && /^[a-z0-9]+$/.test(id)
}

/** A JSON value, as PocketBase's `json` fields hold them. */
export type Json = string | number | boolean | null | { [key: string]: Json } | Json[]

/**
 * The fields PocketBase puts on every record. `expand` is whatever the request
 * asked for, so each record type narrows it rather than sharing one shape.
 */
interface BaseRecord {
  id: string
  collectionId: string
  collectionName: string
}

export interface UserRecord extends BaseRecord {
  /** The username. There is no `profiles` collection; this is it. */
  name: string
  /** Only ever populated for the signed-in user: `emailVisibility` is false. */
  email?: string
  verified: boolean
}

export interface CharacterRecord extends BaseRecord {
  user: string
  /** The sheet. `data` and not `character`, which was a reserved word in Postgres. */
  data: Json
  meta: Json | null
  saved_at: number
  /** The capability id while shared; empty string when not. */
  share: string
}

export interface PortraitRecord extends BaseRecord {
  owner: string
  /** The stored filename, which PocketBase suffixes to keep it unguessable. */
  image: string
}

export interface PreferenceRecord extends BaseRecord {
  user: string
  roll_settings: Json | null
  app_settings: Json | null
}

export interface InviteKeyRecord extends BaseRecord {
  code: string
  /** Empty for a key minted directly by a superuser (the admin-dashboard path). */
  created_by: string
  /** Empty until spent. */
  redeemed_by: string
  /**
   * The username the account registered with, set once at redemption.
   * `expand: 'redeemed_by'` cannot do this: `users.viewRule` only grants
   * visibility between party members, and an inviter shares no party with a
   * stranger they invited, so the expand resolves to nothing. This is the
   * readable name recorded instead, the same trick `PackContentRef` uses.
   */
  redeemed_username: string
  created: string
}

export interface PartyRecord extends BaseRecord {
  name: string
  owner: string
  members_can_see_characters: boolean
  created: string
}

export interface PartyMemberRecord extends BaseRecord {
  party: string
  user: string
  role: 'owner' | 'member'
  /** Empty string when this member has not linked a character. */
  character: string
  joined: string
  /** Present when the request asked to `expand: 'user'`, which is how a roster gets its usernames. */
  expand?: { user?: UserRecord }
}

export interface PartyInviteRecord extends BaseRecord {
  party: string
  invited_by: string
  kind: 'email' | 'link'
  email: string
  code: string
  accepted_by: string
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired'
  /** 0 means unlimited, which is what NULL meant in Postgres. */
  max_uses: number
  used_count: number
  expires: string
  created: string
  expand?: { invited_by?: UserRecord }
}

export interface PartySubscriptionRecord extends BaseRecord {
  party: string
  pointer: Json
  added_by: string
  created: string
}

/** What `GET /api/tomf/shared/{share}` answers with. */
export interface SharedCharacterResponse {
  data: Json
  meta: Json | null
  saved_at: number
}

/** What `GET /api/tomf/invites/preview` answers with. */
export interface InvitePreviewResponse {
  party: string
  name: string
  inviter: string
}
